// outbox-labels.js — plain-English descriptions of queued changes.
// ---------------------------------------------------------------------
// The outbox stores raw HTTP intent (method + path + body). That is fine
// for replaying, and useless for answering the only question a user ever
// asks about a sync queue: "what is actually waiting, and what just went
// through?".
//
// This module turns one queued record into a short human sentence. It is
// deliberately PURE — no DOM, no storage, no imports beyond the status
// vocabulary — so it can be unit-tested in plain node and reused by any
// surface (banner, panel, future activity log).
//
// Two hard rules:
//
//  1. NEVER INVENT MEANING. If the path is not one we recognise we say
//     "Change to /api/whatever", not a guess. A wrong-but-confident label
//     on a sync queue is worse than no label.
//
//  2. NEVER ECHO A BODY VALUE BLINDLY. Queued photo bodies carry
//     multi-megabyte base64 data URLs; other bodies can carry free text
//     of any length. Values are only shown through `safeValue()`, which
//     drops anything long, non-scalar, or data-URI shaped.
//
// Vocabulary note: DropPin is sold standalone, so the nouns here are the
// product's own (pin / plan / location / report) and never a downstream
// recipient's. InSpec is just one place a report can end up.

import { statusLabel } from './defectStatus.js';
// v0.171.0 — a queued photo can name a pin that has not been created yet
// (outbox-refs.js). Naming it "[object Object]" is not a label.
import { isRef } from './outbox-refs.js';

// ── Resource vocabulary ───────────────────────────────────────────────
// API segment → the noun DropPin's own UI uses for that thing.
const RESOURCE_NOUN = {
  'defects':                 'Pin',
  'defect-photos':           'Photo',
  'drawings':                'Plan',
  'projects':                'Location',
  'reports':                 'Report',
  'report-shares':           'Report share',
  'contacts':                'Contact',
  'project-contacts':        'Location contact',
  'report-contacts':         'Report team member',
  'project-team':            'Team member',
  'report-team':             'Team member',
  'contact-invite':          'Contact invite',
  'contact-defect-rectify':  'Rectification',
  'raise-enquiry':           'Enquiry',
  'import-locations':        'Location import',
  'import-location-plan':    'Plan import',
  'tenant':                  'Account settings',
  'account':                 'Account',
};

// Field name → how a person would say it. Only used to explain a generic
// update ("Pin 12 updated — status, notes"); unknown fields fall through
// to the raw key rather than being dropped, so nothing is hidden.
const FIELD_LABEL = {
  markX: 'position', markY: 'position',
  status: 'status', assignedTo: 'assignee', assignee: 'assignee',
  description: 'description', notes: 'notes', comment: 'comment',
  locationLabel: 'location', trade: 'trade', priority: 'priority',
  dueDate: 'due date', name: 'name', title: 'title', caption: 'caption',
  settingsJson: 'report settings', revokedAt: 'revoked',
  address: 'address', email: 'email', phone: 'phone', company: 'company',
  thumbnailUrl: 'photo', displayUrl: 'photo', url: 'photo',
};

// ── Helpers ───────────────────────────────────────────────────────────

// Split '/api/defects/12?x=1' → ['defects', '12'].
export function pathSegments(path) {
  const clean = String(path || '').split('?')[0].split('#')[0];
  return clean.split('/').filter(Boolean).filter(s => s !== 'api');
}

// A body value is only ever shown if it is a short, plain scalar. Long
// strings, objects, arrays and data: URIs are never rendered — a queued
// photo body is megabytes of base64 and would wreck the panel.
export function safeValue(v) {
  if (v == null) return null;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s) return null;
  if (s.length > 40) return null;
  if (/^data:/i.test(s)) return null;
  if (/^https?:\/\//i.test(s) && s.length > 30) return null;
  return s;
}

function fieldList(body) {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return [];
  const seen = new Set();
  const out = [];
  for (const k of Object.keys(body)) {
    const label = FIELD_LABEL[k] || k;
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

function has(body, key) {
  return !!body && typeof body === 'object' && body[key] !== undefined;
}

// ── The main event ────────────────────────────────────────────────────
//
// describeQueuedChange({ method, path, body }) → { title, detail }
//
//   title  — one short sentence, e.g. "Photo added to pin 12".
//   detail — optional second line naming the parent thing it belongs to,
//            e.g. "Location 74". Null when we know nothing extra.
//
// Never throws: a malformed record still yields an honest fallback.
export function describeQueuedChange(item) {
  try {
    return describe(item);
  } catch (_e) {
    const m = item && item.method ? String(item.method).toUpperCase() : 'Change';
    const p = item && item.path ? String(item.path) : 'unknown';
    return { title: `${m} ${p}`, detail: null };
  }
}

function describe(item) {
  const method = String(item?.method || '').toUpperCase();
  const path   = String(item?.path || '');
  const body   = item?.body && typeof item.body === 'object' ? item.body : null;
  const segs   = pathSegments(path);
  const res    = segs[0] || '';
  const id     = segs[1] || null;
  const noun   = RESOURCE_NOUN[res] || null;

  const detail = parentDetail(res, body);

  // Unrecognised endpoint — say exactly that, guess nothing.
  if (!noun) {
    return { title: `Change to ${path || 'an unknown address'} (${method || '?'})`, detail };
  }

  // ── Pins (defects) ──────────────────────────────────────────────
  if (res === 'defects') {
    if (method === 'POST')   return { title: pinCreatedTitle(body), detail };
    if (method === 'DELETE') return { title: `Pin ${id ?? ''}`.trim() + ' deleted', detail };
    if (method === 'PATCH' || method === 'PUT') {
      const who = id ? `Pin ${id}` : 'Pin';
      if (has(body, 'markX') || has(body, 'markY')) return { title: `${who} moved on the plan`, detail };
      if (has(body, 'status')) {
        const s = safeValue(body.status);
        return { title: `${who} set to ${s ? statusLabel(s) : 'a new status'}`, detail };
      }
      if (has(body, 'assignedTo') || has(body, 'assignee')) {
        const a = safeValue(body.assignedTo ?? body.assignee);
        return { title: a ? `${who} assigned to ${a}` : `${who} assignment cleared`, detail };
      }
      if (has(body, 'locationLabel')) {
        const l = safeValue(body.locationLabel);
        return { title: l ? `${who} location set to "${l}"` : `${who} location cleared`, detail };
      }
      return { title: `${who} updated${fieldsSuffix(body)}`, detail };
    }
  }

  // ── Photos ──────────────────────────────────────────────────────
  if (res === 'defect-photos') {
    const pin = body && (body.defectId ?? body.defectID);
    if (method === 'POST') {
      if (isRef(pin)) return { title: 'Photo for a new pin (not yet synced)', detail };
      return { title: pin != null ? `Photo added to pin ${pin}` : 'Photo added', detail };
    }
    if (method === 'DELETE') {
      return { title: id ? `Photo ${id} removed` : 'Photo removed', detail };
    }
    if (method === 'PATCH' || method === 'PUT') {
      return { title: `Photo ${id ?? ''}`.trim() + ` updated${fieldsSuffix(body)}`, detail };
    }
  }

  // ── Everything else: generic but accurate ───────────────────────
  const named = safeValue(body?.name || body?.title);
  if (method === 'POST' && !id) {
    return { title: named ? `New ${noun.toLowerCase()} "${named}"` : `New ${noun.toLowerCase()} added`, detail };
  }
  if (method === 'POST') {
    return { title: `${noun} ${id} — action sent`, detail };
  }
  if (method === 'DELETE') {
    return { title: id ? `${noun} ${id} deleted` : `${noun} deleted`, detail };
  }
  if (method === 'PATCH' || method === 'PUT') {
    if (named) return { title: `${noun}${id ? ' ' + id : ''} renamed to "${named}"`, detail };
    return { title: `${noun}${id ? ' ' + id : ''} updated${fieldsSuffix(body)}`, detail };
  }
  return { title: `Change to ${path} (${method || '?'})`, detail };
}

function pinCreatedTitle(body) {
  const where = safeValue(body?.locationLabel);
  const desc  = safeValue(body?.description);
  if (where) return `New pin dropped at ${where}`;
  if (desc)  return `New pin dropped — "${desc}"`;
  return 'New pin dropped';
}

function fieldsSuffix(body) {
  const fields = fieldList(body).slice(0, 3);
  if (!fields.length) return '';
  return ` — ${fields.join(', ')}`;
}

// The parent thing a change belongs to, when the body names one. Keeps
// "Photo added to pin 12" from being ambiguous across locations.
function parentDetail(res, body) {
  if (!body) return null;
  const bits = [];
  if (body.projectId != null && res !== 'projects') bits.push(`Location ${body.projectId}`);
  if (body.reportId  != null && res !== 'reports')  bits.push(`Report ${body.reportId}`);
  if (body.drawingId != null && res !== 'drawings') bits.push(`Plan ${body.drawingId}`);
  return bits.length ? bits.join(' · ') : null;
}

// ── Time ──────────────────────────────────────────────────────────────
//
// Rows queued before this feature existed have no `queuedAt`. We say
// "queued earlier" for those rather than inventing a date — a wrong
// timestamp on a sync queue is exactly the kind of thing that makes a
// user distrust the whole screen.
export function queuedAtLabel(queuedAt, now) {
  const t = Number(queuedAt);
  if (!Number.isFinite(t) || t <= 0) return 'queued earlier';
  const ref = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  const secs = Math.round((ref - t) / 1000);
  if (secs < 0)    return 'queued just now';
  if (secs < 45)   return 'queued just now';
  if (secs < 5400) {
    const mins = Math.round(secs / 60);
    return `queued ${mins} min ago`;
  }
  if (secs < 86400) {
    const hrs = Math.round(secs / 3600);
    return `queued ${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  }
  const days = Math.round(secs / 86400);
  if (days <= 30) return `queued ${days} day${days === 1 ? '' : 's'} ago`;
  try {
    return `queued ${new Date(t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  } catch (_e) {
    return 'queued earlier';
  }
}
